// ====================================================================
// CHOCKY NPC — a standalone Chocky-model clone for quest-givers
// ====================================================================
// A quest-giver that IS Chocky: loads its own copy of chocky.glb (already
// browser/service-worker cached, so it's just a re-parse — fully independent
// rig, skeleton and clips, with no risk of stepping on the player's model),
// tints the body, optionally scales it up (bigger/taller), pins on a modelled
// outfit (src/game/outfits.js), and plays the baked Idle clip so the NPC
// breathes like the player instead of standing frozen.
//
// Its own module, not grown into main.js (project rule). Returns
// { group, update }: position `group`, then call update(dt) every play frame
// (it ticks the idle mixer). The caller still owns facing/turning, as the
// box-built givers do.

import * as THREE from 'three';
import { makeGLTFLoader } from '../core/gltf.js';
import { ULTRA_LOW_SHADOWS } from '../core/tier.js';
import { heightAt } from '../world/heightfield.js';
import { markDynamic } from '../world/freezeStatics.js';
import { attachOutfitToRig } from './outfits.js';

const TARGET_HEIGHT = 1.8;   // matches the player rig's auto-scale (see main.js)
const CHOCKY_EMISSIVE = 0.18; // the gentle albedo-following lift main.js uses

// The rig's bones are namespaced (e.g. "Chocky_rig_v07:head_Jnt"); resolve the
// outfit joints by suffix, like main.js's bone aliases.
const JOINT_SUFFIX = {
  Head: 'head_Jnt', Spine01: 'spine1_Jnt',
  L_Upperarm: 'shoulder_l_Jnt', R_Upperarm: 'shoulder_r_Jnt',
};

// Mesh-only world bounding box (bones can reach past the visible mesh — using
// them for the box gives a floating model). Same trick as main.js.
function meshOnlyBox(obj) {
  obj.updateMatrixWorld(true);
  const b = new THREE.Box3();
  obj.traverse((o) => {
    if (o.isMesh && o.geometry) {
      if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
      b.union(o.geometry.boundingBox.clone().applyMatrix4(o.matrixWorld));
    }
  });
  return b;
}

export function buildChockyNpc({
  parent, x, z,
  tint = 0xffffff,
  scale = 1,            // uniform size multiplier on top of the height-fit
  outfitModel = null, outfitPieces = [],
  idleClip = 'Idle',
}) {
  const group = new THREE.Group();
  markDynamic(group);   // quests bob/turn NPCs, and the rig animates inside
  group.position.set(x, heightAt(x, z), z);
  parent.add(group);

  const api = {
    group,
    mixer: null,
    ready: false,
    update(dt) { if (api.mixer) api.mixer.update(dt); },
  };

  makeGLTFLoader().load('chocky.glb', (gltf) => {
    const root = gltf.scene;
    let skinned = null;
    root.traverse((o) => {
      if (o.isSkinnedMesh) skinned = o;
      if (o.isMesh) {
        o.castShadow = !ULTRA_LOW_SHADOWS;
        o.receiveShadow = false;
        // The NPC stands far from the origin; its bind-pose bounding volumes
        // don't track the posed/placed mesh well, so skip frustum culling
        // (one NPC — negligible) rather than risk it vanishing at some angles.
        o.frustumCulled = false;
        if (o.material && o.material.map) {
          // A fresh, cheap Lambert material (kid-build budget; also reliably
          // skinning-safe) tinted to the NPC colour, keeping Chocky's face
          // texture. A brand-new instance never touches the player's shared one.
          o.material.map.colorSpace = THREE.SRGBColorSpace;
          o.material = new THREE.MeshLambertMaterial({
            map: o.material.map,
            color: tint,
            emissive: new THREE.Color(tint),
            emissiveMap: o.material.map,
            emissiveIntensity: CHOCKY_EMISSIVE,
          });
        }
      }
    });

    // Fit to the player's height (× the size multiplier), then drop the feet
    // to the group origin so it stands on the terrain `group` was placed at.
    // The scale MUST stay uniform: a non-uniform scale on a skinned-mesh root,
    // composed with this rig's rotated bone frames, collapses the skin. "Taller"
    // is therefore "a bit bigger overall", which reads fine under the tall hat.
    const size = meshOnlyBox(root).getSize(new THREE.Vector3());
    const s = (size.y > 0.01 ? TARGET_HEIGHT / size.y : 1) * scale;
    root.scale.setScalar(s);
    const box = meshOnlyBox(root);
    root.position.x -= (box.min.x + box.max.x) / 2;
    root.position.z -= (box.min.z + box.max.z) / 2;
    root.position.y -= box.min.y;
    group.add(root);
    root.updateMatrixWorld(true);

    // Pin on the outfit while still in rest pose (before the idle clip runs).
    if (outfitModel && outfitPieces.length && skinned?.skeleton) {
      const bones = {};
      for (const [name, suffix] of Object.entries(JOINT_SUFFIX)) {
        const bone = skinned.skeleton.bones.find((b) => b.name.toLowerCase().endsWith(suffix.toLowerCase()));
        if (bone) bones[name] = bone;
      }
      attachOutfitToRig(outfitModel, outfitPieces, root, bones, { castShadow: !ULTRA_LOW_SHADOWS });
    }

    // A little life: play the baked Idle so he breathes like the player.
    if (gltf.animations?.length) {
      api.mixer = new THREE.AnimationMixer(root);
      const clip = gltf.animations.find((a) => a.name === idleClip) || gltf.animations[0];
      if (clip) api.mixer.clipAction(clip).play();
    }
    api.ready = true;
  });

  return api;
}
